import {
  APP_CONFIG,
  ROUTE_ACCOUNT_VIEW,
  ROUTE_CASH_SCREEN,
  YODLEE_ACCOUNT_NAME_LIMIT,
  YODLEE_ACCOUNT_NICKNAME_LIMIT,
} from '@1bill-app/constants';
import { environment } from '@1bill-app/env';
import { objDeepEquals } from '@1bill-app/helpers/object.helper';
import { Component, NgZone, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NavController, ToastController } from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { YodleeQuery } from '@1bill-app/services/yodlee/yodlee.query';
import { YodleeService } from '@1bill-app/services/yodlee/yodlee.service';
import { YodleeStore } from '@1bill-app/services/yodlee/yodlee.store';
import { YodleeAccount } from '@1bill-app/services/yodlee/yodlee.types';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-add-edit-manual-account',
  templateUrl: './add-edit-manual-account.page.html',
  styleUrls: ['./add-edit-manual-account.page.scss'],
})
export class AddEditManualAccountPage implements OnInit {
  constructor(
    private yodleeQuery: YodleeQuery,
    private yodleeStore: YodleeStore,
    private logger: NGXLogger,
    private yodleeService: YodleeService,
    private toastCtrl: ToastController,
    private zone: NgZone,
    private activatedRoute: ActivatedRoute,
    private navControl: NavController,
    private router: Router,
  ) {
    this.initForm();
  }

  accountNameLimit = YODLEE_ACCOUNT_NAME_LIMIT;
  isFormUntouched: boolean = true;
  manualAccountForm: FormGroup;
  saving: boolean;
  subs = new SubSink();
  id: string;
  accountDetails$ = this.yodleeQuery.accountDetails$;
  accountDetails: YodleeAccount;
  edit: boolean;
  shortenedName: string;
  private _wealthType: 'asset' | 'liability' = 'asset';

  /**
   * A list of symbols that the Yodlee API does not allow creating a name for.
   * Yodlee API will return an "Invalid Input" error when attempting to create
   * a name with one of these symbols.
   */
  private readonly INVALID_STRING_SYMBOLS = [','];

  readonly CREDIT_CARD_FORM_VALUE = 'creditCard';

  get wealthType() {
    return this._wealthType;
  }

  set wealthType(input: 'asset' | 'liability') {
    this._wealthType = input;
    this.manualAccountForm.patchValue({ accountType: null });
  }

  set wealthTypeInitial(input: 'asset' | 'liability') {
    this._wealthType = input;
  }

  get isCreditCardSelected() {
    return this.manualAccountForm.get('accountType').value === this.CREDIT_CARD_FORM_VALUE;
  }

  get accountNameControl() {
    return this.manualAccountForm.get('accountName');
  }

  get accountNameInvalidSymbol() {
    return this.INVALID_STRING_SYMBOLS.find((symbol) =>
      (this.accountNameControl.value as string)?.includes(symbol),
    );
  }

  get title() {
    if (this.edit) return 'Edit Manual Account';
    else return 'Add Manual Account';
  }

  ngOnInit() {
    this.id = this.activatedRoute.snapshot.paramMap.get('id'); //get id
    this.logger.log('MY ID: ', this.id);

    //If editing account, get Account based on ID
    if (this.id != 'add') {
      this.edit = true;
      this.subs.sink = this.yodleeService.getAccounts().subscribe();
      //this.id = Number(this.route.snapshot.queryParams?.id);
      if (this.id) this.yodleeStore.update({ currentAccountId: Number(this.id) });
      this.accountDetails$.subscribe((accountDetails) => {
        if (accountDetails) {
          this.accountDetails = accountDetails;
          this.wealthTypeInitial = accountDetails?.wealthType;
        }
      });
      this.manualAccountForm.setValue({
        accountType:
          this.accountDetails?.accountType?.charAt(0).toLowerCase() +
          this.accountDetails?.accountType?.slice(1).replace(/\s/g, ''),
        accountName: this.accountDetails?.accountName,

        accountValue: this.accountDetails?.balance?.amount,
      });
    } else this.edit = false;

    this.manualAccountForm.valueChanges.subscribe(
      (result: {
        accountType: string;
        accountName: string;
        accountValue: number;
        creditCardIsPositive: boolean;
      }) => {
        /**
         * Set this as true if the Yodlee account `nickname` property is used to display names in the app,
         * rather than `accountName`. At the moment, entering 50-100 characters names will have no impact
         * on how the name is displayed within the app, but the Yodlee API `nickname` property has a limit
         * of 50 characters, so it can only be used to display up to that many characters, which will be
         * cut off with an ellipsis.
         */
        const displayCharacterLimitWarning = true;
        if (
          result.accountName?.length > YODLEE_ACCOUNT_NICKNAME_LIMIT &&
          displayCharacterLimitWarning
        ) {
          this.shortenedName = `${result.accountName.substring(
            0,
            YODLEE_ACCOUNT_NICKNAME_LIMIT - 3,
          )}...`;
        } else {
          this.shortenedName = null;
        }

        if (
          !this.edit &&
          objDeepEquals(
            {
              accountType: '',
              accountName: '',
              accountValue: '',
            },
            result,
          )
        ) {
          this.isFormUntouched = true;
          if (
            this.edit &&
            objDeepEquals(
              {
                accountType: this.accountDetails.accountType.toLowerCase(),
                accountName: this.accountDetails.accountName,
                accountValue: this.accountDetails.availableBalance.amount,
              },
              result,
            )
          ) {
            this.isFormUntouched = true;
          }
        } else {
          // this.logger.log('value changed');
          this.isFormUntouched = false;
        }
      },
    );
  }

  initForm() {
    this.isFormUntouched = true;

    this.manualAccountForm = new FormGroup({
      accountType: new FormControl(null, [Validators.required]),
      accountName: new FormControl(null, [Validators.required]),
      accountValue: new FormControl(null, [Validators.required]),
      creditCardIsPositive: new FormControl(false),
    });
  }

  async onSave() {
    try {
      // Modify values based on positive liability checkbox
      if (
        this.isCreditCardSelected &&
        !this.manualAccountForm.get('creditCardIsPositive').value
      ) {
        this.manualAccountForm.patchValue({
          accountValue: -this.manualAccountForm.get('accountValue').value,
        });
      }

      const formData = {
        ...this.manualAccountForm.value,
      };

      this.logger.log({ formData });
      this.saving = true;

      if (this.edit) {
        await this.yodleeService.editManualAccount(formData, Number(this.id)).toPromise();
        // Reload bank account data
        await this.zone.run(async () => {
          await this.yodleeService.getAccounts().toPromise();
          this.showSuccess('Manual account has been added.', 'success');
          this.yodleeQuery.accountListUpdated$.next(formData.accountType);
        });
      } else {
        await this.yodleeService.createManualAccount(formData).toPromise();
        // Reload bank account data
        await this.zone.run(async () => {
          await Promise.all([
            this.yodleeService.getAccounts().toPromise(),
            this.yodleeService.getDashboardWealthData().toPromise(),
          ]);
          this.showSuccess('Manual account has been added.', 'success');
          this.yodleeQuery.accountListUpdated$.next(formData.accountType);
        });
      }

      //await this.refreshData();

      this.saving = false;

      if (this.edit) {
        this.router.navigate([ROUTE_ACCOUNT_VIEW], {
          queryParams: { id: this.id },
          replaceUrl: true,
        });
      } else {
        this.logger.log('type:', formData.accountType);
        await this.router.navigate([ROUTE_CASH_SCREEN], {
          queryParams: { type: formData.accountType },
          replaceUrl: true,
        });
      }
    } catch (err) {
      if (!environment.production) {
        console.error(err);
      }
      // send error log to server
      this.logger.error('id onSave', err);
      // display error toast
      this.showError('Error occurred while saving manual account!');
    }
    this.saving = false;
  }

  async showSuccess(message: string, color = undefined) {
    if (!message) {
      return;
    }
    const toast = await this.toastCtrl.create({
      message,
      duration: APP_CONFIG.TOAST_ERROR_DURATION,
      color: color,
      cssClass: ['toast-success'],
    });
    toast.present();
  }

  async showError(err: any, danger = false) {
    if (!err) {
      return;
    }
    const message = err.statusText || err.message || err;
    const toast = await this.toastCtrl.create({
      message,
      duration: APP_CONFIG.TOAST_ERROR_DURATION,
      color: danger ? 'danger' : 'warning',
      // cssClass: ['toast-error']
    });
    await toast.present();
  }

  async refreshData() {
    const formData = {
      ...this.manualAccountForm.value,
    };

    this.subs.sink = await this.yodleeService.getAccounts().subscribe();
    await this.yodleeStore.update({ currentAccountId: +this.id });
    this.accountDetails$.subscribe((accountDetails) => {
      this.accountDetails = accountDetails;
    });
    await this.yodleeService
      .getAccountViewData('', 'Y', this.id.toString(), formData.accountType)
      .subscribe();
  }
}
